import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeltaBadge } from './oi-delta';

const { Text } = Typography;

export type FinancialDataSource = 'ledger' | 'estimate' | 'hybrid';

export interface ComparativeLineRow {
  key: string;
  label: string;
  amounts: number[];
  pct_of_revenue?: (number | null)[];
  indent?: number;
  is_total?: boolean;
}

interface ComparativeStatementTableProps {
  title?: string;
  periodLabels: string[];
  rows: ComparativeLineRow[];
  sources?: Record<string, FinancialDataSource>;
  formatAmount: (value: number) => string;
  showPctOfRevenue?: boolean;
  showYoYDelta?: boolean;
}

function sourceTag(source?: FinancialDataSource) {
  if (!source || source === 'ledger') return null;
  const color = source === 'estimate' ? 'orange' : 'blue';
  const label = source === 'estimate' ? 'Est.' : 'Hybrid';
  return (
    <Tag color={color} style={{ marginLeft: 6, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
      {label}
    </Tag>
  );
}

export const ComparativeStatementTable: React.FC<ComparativeStatementTableProps> = ({
  title,
  periodLabels,
  rows,
  sources,
  formatAmount,
  showPctOfRevenue = true,
  showYoYDelta = true,
}) => {
  const columns: ColumnsType<ComparativeLineRow> = [
    {
      title: title ?? 'Line item',
      dataIndex: 'label',
      key: 'label',
      fixed: 'left',
      width: 260,
      render: (label: string, row) => (
        <span style={{ paddingLeft: (row.indent ?? 0) * 16, fontWeight: row.is_total ? 600 : 400 }}>
          {label}
          {sources ? sourceTag(sources[row.key]) : null}
        </span>
      ),
    },
    ...periodLabels.map((pl, colIdx) => ({
      title: pl,
      key: `period_${colIdx}`,
      align: 'right' as const,
      render: (_: unknown, row: ComparativeLineRow) => {
        const amount = row.amounts[colIdx] ?? 0;
        const pct = row.pct_of_revenue?.[colIdx];
        const isNegative = amount < 0;
        return (
          <div>
            <Text
              className="oi-mono-value"
              style={{ color: isNegative ? 'var(--oi-crimson)' : undefined, fontWeight: row.is_total ? 600 : 400 }}
            >
              {formatAmount(amount)}
            </Text>
            {showPctOfRevenue && pct != null && row.key !== 'cash_balance' ? (
              <div style={{ fontSize: 11, color: 'var(--oi-text-muted)' }}>{pct.toFixed(0)}%</div>
            ) : null}
          </div>
        );
      },
    })),
  ];

  if (showYoYDelta && periodLabels.length >= 2) {
    columns.push({
      title: 'YoY',
      key: 'yoy',
      align: 'right' as const,
      width: 90,
      render: (_: unknown, row: ComparativeLineRow) => {
        if (!row.is_total && row.key !== 'net_profit' && row.key !== 'revenue_total' && row.key !== 'cash_balance') {
          return null;
        }
        const current = row.amounts[0] ?? 0;
        const prior = row.amounts[1] ?? 0;
        if (prior === 0 && current === 0) return null;
        const deltaPct = prior !== 0 ? ((current - prior) / Math.abs(prior)) * 100 : current !== 0 ? 100 : null;
        return <DeltaBadge deltaPct={deltaPct} />;
      },
    });
  }

  return (
    <Table
      className="oi-comparative-table"
      size="small"
      pagination={false}
      rowKey="key"
      dataSource={rows}
      columns={columns}
      rowClassName={(row) => (row.is_total ? 'oi-comparative-table__total-row' : '')}
      scroll={{ x: periodLabels.length * 140 + 320 }}
    />
  );
};

/** Merge per-period line arrays into row-oriented table data. */
export function mergeComparativeLines(
  periods: Array<{ pl_lines?: Array<{ key: string; label: string; amount: number; pct_of_revenue?: number | null; indent?: number; is_total?: boolean }> }>,
  lineKeyPrefix: 'pl_lines' | 'bs_lines' = 'pl_lines'
): ComparativeLineRow[] {
  const order: string[] = [];
  const map = new Map<string, ComparativeLineRow>();

  for (let i = 0; i < periods.length; i++) {
    const lines = (periods[i][lineKeyPrefix] ?? []) as Array<{
      key: string;
      label: string;
      amount: number;
      pct_of_revenue?: number | null;
      indent?: number;
      is_total?: boolean;
    }>;
    for (const line of lines) {
      if (!map.has(line.key)) {
        order.push(line.key);
        map.set(line.key, {
          key: line.key,
          label: line.label,
          amounts: new Array(periods.length).fill(0),
          pct_of_revenue: new Array(periods.length).fill(null),
          indent: line.indent,
          is_total: line.is_total,
        });
      }
      const row = map.get(line.key)!;
      row.amounts[i] = line.amount;
      row.pct_of_revenue![i] = line.pct_of_revenue ?? null;
      if (line.label) row.label = line.label;
    }
  }

  return order.map((k) => map.get(k)!);
}
